<template>
    <div class="signup-analytics">
      <div class="cards-container">
        <!-- Unique Parents Card -->
        <AnalyticsCard
          title="Parents Signups"
          icon="mdi-account-group"
          :raw-data="signupData"
          date-field="signup_period"
          value-field="unique_parents"
          aggregation-method="sum"
          :stats-config="parentsStatsConfig"
        />
  
        <!-- Total Appointments Card -->
        <AnalyticsCard
          title="Total Appointments"
          icon="mdi-calendar-check"
          :raw-data="signupData"
          date-field="signup_period"
          value-field="total_appointments"
          aggregation-method="sum"
          :stats-config="appointmentsStatsConfig"
        />
  
        <!-- Most Popular Appointment Hour by Percentage Card -->
        <AnalyticsCard
          title="Most Popular Appointment Hour by Percentage"
          icon="mdi-clock-outline"
          :raw-data="popularTimeData"
          date-field="appointment_hour"
          value-field="percent_of_total"
          aggregation-method="average"
          :stats-config="popularTimePercentageStatsConfig"
          :tooltip-formatter="(value) => `${value.toFixed(2)}%`"
        />
  
        <!-- Most Popular Appointment Hour by Teacher Card -->
        <AnalyticsCard
          title="Most Popular Appointment Hour by Teacher"
          icon="mdi-account-multiple"
          :raw-data="popularTimeData"
          date-field="appointment_hour"
          value-field="num_teachers"
          aggregation-method="none"
          :stats-config="popularTimeTeachersStatsConfig"
        />
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import dayjs from "dayjs";
  import FetchService from "@/services/fetch.service";
  import AnalyticsCard from "./AnalyticsCard.vue";
  
  type SignupDataItem = {
    signup_period: string;
    unique_parents: string;
    total_appointments: string;
  };
  
  type PopularTimeDataItem = {
    appointment_hour: string;
    total_slots: number;
    percent_of_total: number;
    num_teachers: number;
  };
  
  type SignUpRateResponse = {
    table_data?: SignupDataItem[];
    // Conference-level distinct parent count (avoids double-counting across hours)
    summary?: {
      total_unique_parents?: number | string;
    };
  };
  
  const props = defineProps<{
    eventId: string;
  }>();
  
  const signupData = ref<SignupDataItem[]>([]);
  const popularTimeData = ref<PopularTimeDataItem[]>([]);
  // null = summary not present yet (staged deploy / older API); prefer summary when set
  const totalUniqueParents = ref<number | null>(null);
  
  type StatConfig = {
    label: string;
    compute: (data: number[], aggregatedData: Array<{ period: string; value: number }>) => string | number;
  };
  
  // Statistics configuration for each card
  const parentsStatsConfig = computed<StatConfig[]>(() => [
    {
      label: "Total Parents",
      compute: (data) => {
        // Prefer API summary.total_unique_parents (conference-level COUNT DISTINCT).
        // Do not sum hourly unique_parents — that double-counts parents across hours.
        if (totalUniqueParents.value !== null) {
          return totalUniqueParents.value;
        }
        // Staged-deploy fallback when summary is missing: legacy hourly sum
        return data.reduce((sum, val) => sum + val, 0);
      },
    },
    {
      label: "Peak Period",
      compute: (data, aggregatedData) => {
        const maxValue = Math.max(...data);
        const maxIndex = data.indexOf(maxValue);
        if (maxIndex < 0 || !aggregatedData[maxIndex]) return "N/A";
        const date = dayjs(aggregatedData[maxIndex].period);
        // Format based on whether it's hourly, daily, or weekly (check period format)
        if (aggregatedData[maxIndex].period.includes(" ")) {
          return date.format("MMM DD, YYYY HH:mm");
        } else if (aggregatedData[maxIndex].period.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date.format("MMM DD, YYYY");
        } else {
          return `Week of ${date.format("MMM DD, YYYY")}`;
        }
      },
    },
    {
      label: "Peak Value",
      compute: (data) => Math.max(...data),
    },
    {
      label: "Average per Period",
      compute: (data) => (data.length > 0 ? data.reduce((sum, val) => sum + val, 0) / data.length : 0),
    },
  ]);
  
  const appointmentsStatsConfig: StatConfig[] = [
    {
      label: "Total Appointments",
      compute: (data) => data.reduce((sum, val) => sum + val, 0),
    },
    {
      label: "Peak Period",
      compute: (data, aggregatedData) => {
        const maxValue = Math.max(...data);
        const maxIndex = data.indexOf(maxValue);
        if (maxIndex < 0 || !aggregatedData[maxIndex]) return "N/A";
        const date = dayjs(aggregatedData[maxIndex].period);
        if (aggregatedData[maxIndex].period.includes(" ")) {
          return date.format("MMM DD, YYYY HH:mm");
        } else if (aggregatedData[maxIndex].period.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date.format("MMM DD, YYYY");
        } else {
          return `Week of ${date.format("MMM DD, YYYY")}`;
        }
      },
    },
    {
      label: "Peak Value",
      compute: (data) => Math.max(...data),
    },
    {
      label: "Average per Period",
      compute: (data) => (data.length > 0 ? data.reduce((sum, val) => sum + val, 0) / data.length : 0),
    },
  ];
  
  const popularTimePercentageStatsConfig: StatConfig[] = [
    {
      label: "Peak Hour",
      compute: (data, aggregatedData) => {
        const maxValue = Math.max(...data);
        const maxIndex = data.indexOf(maxValue);
        if (maxIndex < 0 || !aggregatedData[maxIndex]) return "N/A";
        const date = dayjs(aggregatedData[maxIndex].period);
        return date.format("MMM DD, YYYY h:mm A");
      },
    },
    {
      label: "Peak Percentage",
      compute: (data) => `${Math.max(...data).toFixed(1)}%`,
    },
    {
      label: "Average Percentage",
      compute: (data) =>
        `${(data.length > 0 ? data.reduce((sum, val) => sum + val, 0) / data.length : 0).toFixed(1)}%`,
    },
    {
      label: "Total Hours",
      compute: (data) => data.length,
    },
  ];
  
  const popularTimeTeachersStatsConfig: StatConfig[] = [
    {
      label: "Peak Hour",
      compute: (data, aggregatedData) => {
        const maxValue = Math.max(...data);
        const maxIndex = data.indexOf(maxValue);
        if (maxIndex < 0 || !aggregatedData[maxIndex]) return "N/A";
        const date = dayjs(aggregatedData[maxIndex].period);
        return date.format("MMM DD, YYYY h:mm A");
      },
    },
    {
      label: "Peak Teachers",
      compute: (data) => Math.max(...data),
    },
    {
      label: "Average Teachers",
      compute: (data) => (data.length > 0 ? data.reduce((sum, val) => sum + val, 0) / data.length : 0),
    },
    {
      label: "Total Hours",
      compute: (data) => data.length,
    },
  ];
  
  const parseTotalUniqueParents = (summary: SignUpRateResponse["summary"]): number | null => {
    if (!summary || summary.total_unique_parents === undefined || summary.total_unique_parents === null) {
      return null;
    }
    const raw = summary.total_unique_parents;
    const parsed = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Fetch signup data from API
  onMounted(async () => {
    // Fetch signup data from API
    try {
      const response = (await FetchService.fetchData(
        `/reports/stats_sign_up_rate/${props.eventId}`,
        "get"
      )) as SignUpRateResponse;

      totalUniqueParents.value = parseTotalUniqueParents(response?.summary);

      if (response?.table_data && Array.isArray(response.table_data)) {
        // Validate data structure
        const validData = response.table_data.filter(
          (item: any) =>
            item &&
            typeof item.signup_period === "string" &&
            (typeof item.unique_parents === "string" || typeof item.unique_parents === "number") &&
            (typeof item.total_appointments === "string" || typeof item.total_appointments === "number")
        ) as SignupDataItem[];

        signupData.value = validData;
      } else {
        console.warn("Signup analytics API returned invalid data structure");
        signupData.value = [];
      }
    } catch (error) {
      console.error("Error fetching signup analytics data:", error);
      signupData.value = [];
      totalUniqueParents.value = null;
    }
    
    // Fetch popular time data from API
    try {
      const popularTimeResponse = await FetchService.fetchData(
        `/reports/stats_most_popular_time/${props.eventId}`,
        "get"
      ) as { table_data?: PopularTimeDataItem[] };
      
      if (popularTimeResponse?.table_data && Array.isArray(popularTimeResponse.table_data)) {
        // Validate data structure
        const validData = popularTimeResponse.table_data.filter(
          (item: any) =>
            item &&
            typeof item.appointment_hour === "string" &&
            (typeof item.total_slots === "number" || typeof item.total_slots === "string") &&
            (typeof item.percent_of_total === "number" || typeof item.percent_of_total === "string") &&
            (typeof item.num_teachers === "number" || typeof item.num_teachers === "string")
        ).map((item: any) => ({
          appointment_hour: item.appointment_hour,
          total_slots: typeof item.total_slots === "string" ? parseInt(item.total_slots, 10) : item.total_slots,
          percent_of_total: typeof item.percent_of_total === "string" ? parseFloat(item.percent_of_total) : item.percent_of_total,
          num_teachers: typeof item.num_teachers === "string" ? parseInt(item.num_teachers, 10) : item.num_teachers,
        })) as PopularTimeDataItem[];
        
        popularTimeData.value = validData;
      } else {
        console.warn("Popular time API returned invalid data structure");
        popularTimeData.value = [];
      }
    } catch (error) {
      console.error("Error fetching popular time data:", error);
      popularTimeData.value = [];
    }
  });
  </script>
  
  <style scoped lang="scss">
  @use "@/assets/_vars.scss" as *;
  @use "@/assets/_breakpoints.scss" as breakpoints;
  
  .signup-analytics {
    width: 100%;
    margin-top: 20px;
  }
  
  .cards-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  
    @include breakpoints.media-breakpoint-down(md) {
      grid-template-columns: 1fr;
    }
  }
  </style>
